import { getFunctions, httpsCallable } from "firebase/functions";
import app from "../firebase";
import { supabase } from "../lib/supabase";

const functions = getFunctions(app);
interface RedactResult {
  redactedText: string
  studentName: string
  grade: string
  docType: string
  reviewRequired: boolean
  residualCategoriesRemoved: string[]
  chunksProcessed: number
  originalPageCount?: number
  outputPageCount?: number
}

async function getAccessToken(): Promise<string> {
  const { data, error } = await supabase.auth.getSession();
  if (error || !data.session?.access_token) throw new Error("Sign in is required to process documents.");
  return data.session.access_token;
}

const DOC_TOO_LARGE_MESSAGE =
  "This document is too large to process. Try splitting it into smaller sections.";

const DOC_TIMEOUT_MESSAGE =
  "This document took too long to process. Try a shorter section or split the document into smaller parts.";

// Match server timeout (redactText / rewriteReport use 300s)
const CALLABLE_TIMEOUT_MS = 300_000;
const OCR_PAGE_TIMEOUT_MS = 60_000;

const redactTextFunction = httpsCallable<{ text: string; accessToken: string }, RedactResult>(
  functions,
  "redactText",
  { timeout: CALLABLE_TIMEOUT_MS }
);

const rewriteReportFunction = httpsCallable<{ text: string; accessToken: string }, { rewrittenText: string }>(
  functions,
  "rewriteReport",
  { timeout: CALLABLE_TIMEOUT_MS }
);

const ocrPdfPageFunction = httpsCallable<
  { imageBase64: string; pageNumber: number; mimeType?: string; accessToken: string },
  { text: string; pageNumber: number; durationMs: number }
>(functions, "ocrPdfPage", { timeout: OCR_PAGE_TIMEOUT_MS });

type CallableErrorLike = {
  message?: string;
  code?: string;
  details?: unknown;
};

function getCallableErrorMessage(error: unknown, fallback: string): string {
  const err = error as CallableErrorLike;
  const detailsMessage =
    typeof err?.details === "object" &&
    err.details !== null &&
    "message" in err.details &&
    typeof (err.details as { message?: unknown }).message === "string"
      ? (err.details as { message: string }).message
      : undefined;

  const message =
    detailsMessage ||
    (typeof err?.message === "string" && err.message.trim() ? err.message : fallback);

  // Normalize any length/token limit errors into a teacher-friendly message
  const lower = message.toLowerCase();
  if (
    lower.includes("maximum length") ||
    lower.includes("too large") ||
    (lower.includes("token") && lower.includes("limit")) ||
    lower.includes("100,000") ||
    lower.includes("100000") ||
    lower.includes("500,000") ||
    lower.includes("500000")
  ) {
    return DOC_TOO_LARGE_MESSAGE;
  }

  if (
    lower.includes("deadline-exceeded") ||
    lower.includes("deadline exceeded") ||
    lower.includes("timeout") ||
    lower.includes("timed out")
  ) {
    return DOC_TIMEOUT_MESSAGE;
  }

  return message;
}

/**
 * Sends text to the Cloud Function for PII redaction.
 * The API key is securely stored server-side.
 * @param text The text to be redacted.
 * @returns A promise that resolves with the redacted text.
 */
export const redactTextWithGemini = async (text: string): Promise<RedactResult> => {
  try {
    const result = await redactTextFunction({ text, accessToken: await getAccessToken() });
    return result.data;
  } catch (error) {
    console.error("Error calling redaction service:", error);
    throw new Error(
      getCallableErrorMessage(
        error,
        "The AI service failed to process the request. Please try again later."
      )
    );
  }
};

export const rewriteReportWithGemini = async (text: string): Promise<string> => {
  try {
    const result = await rewriteReportFunction({ text, accessToken: await getAccessToken() });
    return result.data.rewrittenText;
  } catch (error) {
    console.error("Error calling rewrite service:", error);
    throw new Error(
      getCallableErrorMessage(
        error,
        "The AI rewrite service failed to process the request. Please try again later."
      )
    );
  }
};

/**
 * Sends a single rasterized page image to the Cloud Function for OCR.
 */
export const ocrPageWithGemini = async (
  imageBase64: string,
  pageNumber: number
): Promise<string> => {
  try {
    const result = await ocrPdfPageFunction({
      imageBase64,
      pageNumber,
      mimeType: "image/jpeg",
      accessToken: await getAccessToken(),
    });
    return result.data.text;
  } catch (error) {
    console.error("Error calling OCR service:", error);
    throw new Error(
      getCallableErrorMessage(
        error,
        "The OCR service failed to process this page. Please try again."
      )
    );
  }
};
